/* eslint-disable react/prop-types */
import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'

const PwaContext = createContext(null)

export function detectPwaEnvironment(windowRef = window) {
  const ua = windowRef.navigator.userAgent || ''
  const isIos = /iPad|iPhone|iPod/.test(ua) || (windowRef.navigator.platform === 'MacIntel' && windowRef.navigator.maxTouchPoints > 1)
  const standalone = windowRef.matchMedia?.('(display-mode: standalone)').matches || windowRef.navigator.standalone === true
  return { isIos, standalone, permission: 'Notification' in windowRef ? windowRef.Notification.permission : 'unsupported' }
}

export function getIosNotificationState({ isIos, standalone, permission }) {
  if (!isIos) return 'not-ios'
  if (!standalone) return 'install-required'
  if (permission === 'default') return 'needs-permission'
  if (permission === 'granted') return 'granted'
  if (permission === 'denied') return 'denied'
  return 'unsupported'
}

function usePwaLifecycleState() {
  const [environment, setEnvironment] = useState(() => detectPwaEnvironment())
  const [installEvent, setInstallEvent] = useState(null)
  const [waitingWorker, setWaitingWorker] = useState(null)
  const [online, setOnline] = useState(navigator.onLine)

  useEffect(() => {
    const beforeInstall = event => { event.preventDefault(); setInstallEvent(event) }
    const installed = () => { setInstallEvent(null); setEnvironment(detectPwaEnvironment()) }
    const onlineChange = () => setOnline(navigator.onLine)
    window.addEventListener('beforeinstallprompt', beforeInstall)
    window.addEventListener('appinstalled', installed)
    window.addEventListener('online', onlineChange); window.addEventListener('offline', onlineChange)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then(registration => {
        if (registration.waiting) setWaitingWorker(registration.waiting)
        registration.addEventListener('updatefound', () => {
          const worker = registration.installing
          worker?.addEventListener('statechange', () => {
            if (worker.state === 'installed' && navigator.serviceWorker.controller) setWaitingWorker(worker)
          })
        })
      }).catch(error => console.error('[AdminPWA] service worker registration failed', error))
    }
    return () => { window.removeEventListener('beforeinstallprompt', beforeInstall); window.removeEventListener('appinstalled', installed); window.removeEventListener('online', onlineChange); window.removeEventListener('offline', onlineChange) }
  }, [])

  const install = useCallback(async () => {
    if (!installEvent) return false
    await installEvent.prompt()
    const choice = await installEvent.userChoice
    setInstallEvent(null)
    return choice.outcome === 'accepted'
  }, [installEvent])
  const update = useCallback(() => {
    navigator.serviceWorker?.addEventListener('controllerchange', () => window.location.reload(), { once: true })
    waitingWorker?.postMessage({ type: 'SKIP_WAITING' })
  }, [waitingWorker])
  return useMemo(() => ({ ...environment, online, canInstall: Boolean(installEvent), updateAvailable: Boolean(waitingWorker), install, update, refresh: () => setEnvironment(detectPwaEnvironment()) }), [environment, installEvent, install, online, update, waitingWorker])
}

export function PwaProvider({ children }) {
  const value = usePwaLifecycleState()
  return createElement(PwaContext.Provider, { value }, children)
}

export function usePwaLifecycle() {
  const value = useContext(PwaContext)
  if (!value) throw new Error('usePwaLifecycle must be used inside PwaProvider')
  return value
}
